/**
 * UC-004 Payment tenant schemas (Phase 19).
 *
 * Type contracts pro `/api/tenant/payment-intents/{id}` REST endpoint.
 * Backend payload returns CZK + EUR paralelně bez FX přepočtu (princip #2).
 *
 * EUR-only měsíc → `qr_png_base64 = null`; UI renderuje EUR advisory bez QR
 * (REQ-BIZ-018 — žádný SPAYD QR pro EUR).
 */

export const PAYMENT_INTENT_STATUS = [
  "open",
  "paid",
  "expired",
] as const;
export type PaymentIntentStatus = (typeof PAYMENT_INTENT_STATUS)[number];

export interface PaymentDrillDownTransaction {
  fuel_transaction_id: string;
  occurred_at: string;
  fuel_card_label: string | null;
  plate_text: string;
  liters: string | null;
  amount_kc: string;
  amount_eur: string | null;
  location: string | null;
}

export interface PaymentDrillDownSpz {
  fuel_card_id: string;
  plate_text: string;
  amount_kc: string;
  amount_eur: string;
  transaction_count: number;
  transactions: PaymentDrillDownTransaction[];
}

export interface PaymentIntentDetailResponse {
  payment_intent_id: string;
  tenant_id: string;
  budget_id: string;
  recipient_user_id: string;
  recipient_email: string;
  month: string;
  billing_group_code: string;
  status: PaymentIntentStatus;
  amount_kc: string;
  amount_eur: string;
  /** TRUE → měsíc obsahuje EUR transakce; QR vždy CZK-only per REQ-BIZ-018. */
  eur_advisory_flag: boolean;
  /** NULL pro EUR-only měsíc (REQ-BIZ-018). */
  qr_png_base64: string | null;
  iban_cz: string;
  variable_symbol: string;
  /** SPAYD MSG sanitized ASCII (UC-003). */
  message: string;
  paid_at: string | null;
  expires_at: string | null;
  created_at: string;
  drill_down: PaymentDrillDownSpz[];
}

export interface PaymentDashboardSummaryResponse {
  open_intent_count: number;
  open_total_kc: string;
  next_intent: {
    payment_intent_id: string;
    amount_kc: string;
    month: string;
  } | null;
  /** Sub-admin/admin only — for David A&L soft view (UC-005). */
  is_a_l_soft: boolean;
}

/**
 * Formátování CZK z plain-decimal stringu backendu.
 * Žádný FX přepočet (princip #2 source-of-truth = Axigon).
 */
export function formatKcPayment(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return (
    n.toLocaleString("cs-CZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " Kč"
  );
}

export function formatEurPayment(raw: string | null | undefined): string {
  if (raw == null || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return (
    n.toLocaleString("cs-CZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
}
